/**
 * Font Style preset picker (PLAT-9973).
 *
 * Three vertically-stacked cards — Title, Subtitle, Text — that preview
 * each preset in its own typographic style. Clicking a card hands the
 * preset to the parent, which applies `font_family` + `font_weight` +
 * `font_size` to the active text element in one go.
 *
 * Sits at the top of the Graphics → Text panel as a "pick a hierarchy
 * first, then fine-tune" entry point. Granular controls (font family,
 * size, alignment, colour) live below and remain unchanged.
 */

import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import { getFontStylePresets } from '../../utility/fontStyles';

import type { FontStylePreset } from '../../utility/fontStyles';

type Props = {
  onSelect: (preset: FontStylePreset) => void;
};

/**
 * `observer` so the picker reactively re-resolves presets when the org's
 * branding (and therefore its Font Style overrides) changes — e.g. after
 * the user saves new values in Brand Kit and navigates back to the
 * editor without a full reload.
 */
export const TextFontStylePicker: React.FC<Props> = observer(({ onSelect }) => {
  const videoCreator = useVideoCreatorStore();
  const presets = getFontStylePresets(videoCreator.organization?.branding);

  return (
    <SSection>
      <SLabel>Default Text Presets</SLabel>
      <SList>
        {presets.map(preset => (
          <SPresetButton
            key={preset.key}
            type="button"
            onClick={() => onSelect(preset)}
            aria-label={`Apply ${preset.label} style`}
            title={`${preset.label}: ${preset.fontFamily} ${preset.fontWeight} · ${preset.fontSize}px`}
            style={{
              fontFamily: preset.fontFamily,
              fontWeight: preset.fontWeight,
              // Preview is rendered at a card-friendly size, *not* the
              // canvas font-size. The canvas (much larger surface) gets
              // `preset.fontSize` when the user clicks — see
              // `handleApplyFontStyle` in TextSettings.
              fontSize: `${preset.previewFontSize}px`,
            }}
          >
            {preset.sample}
          </SPresetButton>
        ))}
      </SList>
    </SSection>
  );
});

export default TextFontStylePicker;

const SSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
`;

const SLabel = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
`;

const SList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

/**
 * Each preset card renders its sample text at the preset's real font
 * size. Overflow is hidden and ellipsised so an over-long sample (or a
 * very large preset) doesn't blow up the card height past expectations.
 * Line-height is tuned tight so the 48px Title card doesn't end up
 * dramatically taller than the 16px Text card.
 */
const SPresetButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: var(--btn-h-xl);
  padding: var(--spacing-md) var(--spacing-md);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-family: var(--font-primary);
  text-align: left;
  line-height: 1.1;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    color 120ms ease;

  /*
   * Sample text shouldn't ever wrap or push the card taller than its own
   * font-size implies. Clip + ellipsis keeps the layout predictable.
   */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    background: var(--surface-elevated);
    border-color: var(--accent);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
`;
